import React, { useEffect, useState } from "react";
import { MicroscopeMark, breathDelay } from "./BrandMark";

/**
 * NativeSplash — animated launch overlay for the iOS app.
 *
 * Capacitor's static splash hands off after ~500ms (config in
 * capacitor.config.json). This component then takes over the screen
 * for ~1.6s before fading out, giving the launch a proper branded
 * animation instead of the default "static image → app" hard cut.
 *
 * The microscope mark is drawn as inline SVG (sourced from
 * public/images/microscope.svg) so we can animate paths and the
 * surrounding glow with pure CSS — no extra HTTP fetch and no
 * decoding flash. Background is locked to the brand near-black so
 * it visually continues the static splash.
 *
 * Render order on iOS app launch:
 *   1. iOS LaunchScreen.storyboard  (instant, ~50–100ms)
 *   2. Capacitor SplashScreen plugin (static microscope, ~500ms)
 *   3. NativeSplash (animated, fades to dashboard, ~1600ms)
 *
 * Pure no-op on web — caller already gates with isNative().
 */
export function NativeSplash({ onDone, holdMs = 1400 }) {
  const [phase, setPhase] = useState("enter"); // enter -> hold -> exit -> gone
  useEffect(() => {
    // enter takes ~600ms, then hold, then exit (350ms), then unmount
    const t1 = setTimeout(() => setPhase("hold"), 600);
    const t2 = setTimeout(() => setPhase("exit"), 600 + holdMs);
    const t3 = setTimeout(() => { setPhase("gone"); onDone?.(); }, 600 + holdMs + 350);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [holdMs, onDone]);

  if (phase === "gone") return null;

  return (
    <div aria-hidden="true" style={{
      position: "fixed", inset: 0, zIndex: 999999,
      background: "#0A0A0A",
      display: "flex", alignItems: "center", justifyContent: "center",
      opacity: phase === "exit" ? 0 : 1,
      transition: "opacity .35s cubic-bezier(.4,0,.2,1)",
      pointerEvents: phase === "exit" ? "none" : "auto",
    }}>
      {/* The glow breathes on the same cycle, pinned to the same clock, as
          the boot loader that follows, so the handoff does not jolt. The
          mark only scales in: a float on a different period than the glow
          was two loops drifting against each other, and the drop-shadow
          filter on a moving SVG was repainted every frame on iOS. */}
      <div aria-hidden="true" className="bam-breathe-glow" style={{
        position: "absolute", width: 520, height: 520, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(226,221,159,0.20) 0%, rgba(226,221,159,0.05) 42%, rgba(226,221,159,0) 70%)",
        animationDelay: breathDelay(),
      }}/>
      <MicroscopeMark size={180} style={{
        position: "relative",
        animation: "splashMarkEnter .65s cubic-bezier(.2,.8,.2,1) both",
      }}/>
      {/* Wordmark fades in after the mark settles. */}
      <div style={{
        position: "absolute",
        bottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
        left: 0, right: 0,
        textAlign: "center",
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: 14,
        letterSpacing: 4.5,
        color: "#E2DD9F",
        opacity: phase === "enter" ? 0 : 0.85,
        transform: phase === "enter" ? "translateY(8px)" : "translateY(0)",
        transition: "opacity .5s ease, transform .5s ease",
      }}>
        BY ANY MEANS
      </div>
    </div>
  );
}

export default NativeSplash;
